// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { TextStyle, ViewStyle } from 'react-native';
import appColors from '@/theme/colors';
import appFonts from '@/theme/typography';

/**
 * Shared type and surface tokens for the tour editor, matching the round-init
 * and round-summary screens so setup does not look like a different product.
 *
 * Per the "Digital Clubhouse" identity: serif for headlines, uppercase tracked
 * sans for labels, and flat surface cards rather than shadows.
 */

// Sanchez is bundled in app/_layout and is what the round summary already uses
// for headlines, so it renders identically on web and native.
export const SERIF_FAMILY = appFonts.brand;

/** Editorial screen headline — the panel title. */
export const headline: TextStyle = {
  fontFamily: SERIF_FAMILY,
  fontSize: 26,
  fontWeight: '700',
  letterSpacing: 0.2,
  color: appColors.text.heading,
};

/** Uppercase tracked label above a group of fields, as in round-init's cards. */
export const sectionLabel: TextStyle = {
  color: appColors.text.primary,
  fontWeight: '800',
  letterSpacing: 1.1,
  textTransform: 'uppercase',
  fontSize: 13,
};

/** Small caption under a heading, for the explanatory line. */
export const caption: TextStyle = {
  color: appColors.text.secondary,
  fontSize: 12,
  letterSpacing: 0.3,
  lineHeight: 17,
};

/** Field label inside a card. */
export const fieldLabel: TextStyle = {
  color: appColors.text.muted,
  fontSize: 11,
  fontWeight: '700',
  letterSpacing: 0.9,
  textTransform: 'uppercase',
};

/** Surface card. Flat with a subtle tonal border — no shadow. */
export const card: ViewStyle = {
  borderRadius: 16,
  padding: 16,
  backgroundColor: appColors.background.panel,
  borderWidth: 1,
  borderColor: appColors.border.strong,
  gap: 12,
};

/**
 * Editable fields carry the accent border, as in round-init — inside a card the
 * darker tonal border disappears and a field stops reading as interactive.
 */
// Deliberately unannotated: this is both spread into TextInput styles and applied
// to a Pressable, and ViewStyle/TextStyle disagree on `userSelect`, so neither
// annotation fits both. The inferred literal type is assignable to each.
export const fieldInput = {
  borderRadius: 12,
  borderWidth: 1,
  borderColor: appColors.accent.primary,
  backgroundColor: appColors.background.panel,
  paddingHorizontal: 12,
  paddingVertical: 10,
};

/** Compact segmented choice, e.g. 9 / 18 holes. */
export const pill: ViewStyle = {
  paddingVertical: 6,
  paddingHorizontal: 14,
  borderRadius: 999,
  borderWidth: 1,
  borderColor: appColors.border.strong,
  backgroundColor: appColors.surface.card,
};

export const pillActive: ViewStyle = {
  borderColor: appColors.accent.secondary,
  backgroundColor: appColors.accent.secondary,
};

export const pillText: TextStyle = {
  color: appColors.text.subtle,
  fontWeight: '600',
};

export const pillTextActive: TextStyle = {
  color: appColors.background.panel,
};
